import React from "react";
import { useMsal } from "@azure/msal-react";
import { Link } from "react-router-dom";
import { useMe } from "../contexts/MeContext";

/**
 * Landing page after sign-in. Role-aware via MeContext:
 *
 *   - Tool Owner section: visible iff me.ownedTools is non-empty. One card per
 *     owned tool, each linking to that tool's Groups Roster.
 *   - Admin section: visible iff me.isAdmin. Three cards (Tools Registry,
 *     System Settings, Active Cycle Dashboard).
 *   - Reviewer section: always shown — anyone might be assigned as a
 *     reviewer on the fly. Explains that review pages are reached via Slack
 *     links, not from this menu.
 *
 * If the user has no role at all (no owned tools, not admin), the Tool Owner
 * and Admin sections are simply omitted and only the Reviewer section is
 * shown. The page still renders cleanly with the "you'll get review links via
 * Slack" message as the only actionable instruction.
 *
 * While /me is loading, the page shows a brief loading state. If /me fails,
 * it shows an error state with a retry button (via MeContext.refetch).
 */
const HomePage: React.FC = () => {
  const { accounts } = useMsal();
  const account = accounts[0];
  const firstName = (account?.name ?? "").split(" ")[0];

  const { me, loading, error, refetch } = useMe();

  return (
    <div style={pageStyle}>
      <h1 style={{ marginTop: 0, color: "#323130" }}>
        Welcome{firstName ? `, ${firstName}` : ""}.
      </h1>
      <p style={{ color: "#605e5c", fontSize: 15, lineHeight: 1.6 }}>
        This portal manages the Entra Access Review process. The sections below are scoped
        to your role.
      </p>

      {loading ? (
        <p style={{ color: "#605e5c", fontStyle: "italic" }}>Loading your portal access…</p>
      ) : error || !me ? (
        <div style={errorBoxStyle}>
          {error ?? "Couldn't load your portal access."}
          <button onClick={refetch} style={retryBtnStyle}>Retry</button>
        </div>
      ) : (
        <>
          {/* Tool Owner section — only if user owns at least one tool */}
          {me.ownedTools.length > 0 && (
            <>
              <h2 style={sectionHeadingStyle}>Tool Owner</h2>
              <div style={cardListStyle}>
                {me.ownedTools.map((toolName) => (
                  <NavCard
                    key={toolName}
                    title={`${toolName} — Groups Roster`}
                    description={`Manage the Entra security groups under ${toolName}. Adding or removing a group here affects future review cycles only — it does not change anyone's actual access.`}
                    to={`/tools/${encodeURIComponent(toolName)}/groups`}
                  />
                ))}
              </div>
            </>
          )}

          {/* Admin section — only if user is in the admin Entra group */}
          {me.isAdmin && (
            <>
              <h2 style={sectionHeadingStyle}>Admin</h2>
              <div style={cardListStyle}>
                <NavCard
                  title="Tools Registry"
                  description="Create, edit, and remove tools. Configure tool owners, cycle frequency, control IDs, and per-stage durations."
                  to="/manage/tools"
                />
                <NavCard
                  title="System Settings"
                  description="Edit system-level configuration such as the AccessReviewAdmins Entra group ID."
                  to="/manage/settings"
                />
                <NavCard
                  title="Active Cycle Dashboard"
                  description="Monitor every in-flight review cycle with progress aggregates. Drill down to individual decisions and extend deadlines when operationally necessary."
                  to="/manage/cycles"
                />
              </div>
            </>
          )}

          {/* Reviewer section — always shown */}
          <h2 style={sectionHeadingStyle}>Reviewer (Stage 1 / Stage 2)</h2>
          {me.activeReviews.length > 0 ? (
            <>
              <p style={{ color: "#605e5c", fontSize: 14, lineHeight: 1.6 }}>
                You have {me.activeReviews.length} active review{me.activeReviews.length === 1 ? "" : "s"} assigned to you.
                These are the same reviews linked from your Slack notifications — open one here if you can't find the Slack message.
              </p>
              <div style={cardListStyle}>
                {me.activeReviews.map((r) => {
                  const href =
                    `/?tool=${encodeURIComponent(r.toolName)}` +
                    `&cycle=${encodeURIComponent(r.cycleName)}` +
                    `&stage=${r.stage}` +
                    `&freq=${encodeURIComponent(r.frequency)}`;
                  const deadlineText = r.deadline
                    ? new Date(r.deadline).toLocaleString(undefined, {
                        day: "2-digit", month: "short", year: "numeric",
                        hour: "2-digit", minute: "2-digit",
                      })
                    : null;
                  return (
                    <a
                      key={`${r.toolName}|${r.cycleName}|${r.stage}`}
                      href={href}
                      style={{ ...cardLinkStyle, ...cardStyle }}
                    >
                      <h3 style={{ margin: 0, fontSize: 16, color: "#0078d4" }}>
                        {r.toolName} — {r.cycleName}
                      </h3>
                      <p style={{ margin: "6px 0 0", fontSize: 13.5, color: "#605e5c", lineHeight: 1.5 }}>
                        Stage {r.stage} review{deadlineText ? ` — due ${deadlineText}` : ""}.
                        Click to open and record your decisions.
                      </p>
                    </a>
                  );
                })}
              </div>
            </>
          ) : (
            <p style={{ color: "#605e5c", fontSize: 14, lineHeight: 1.6 }}>
              You have no active reviews assigned right now. When a cycle starts, you'll get a
              <strong> Begin Review</strong> link in Slack — and any active review will also appear
              here while it's open.
            </p>
          )}

          {/* Fallback: user has no actionable sections */}
          {me.ownedTools.length === 0 && !me.isAdmin && (
            <div style={noRoleNoteStyle}>
              You don't have any tool ownership or admin role configured for this portal right
              now. If you're expecting to manage a tool, ask an administrator to set you as its
              owner. Otherwise, watch for review links in Slack when a cycle begins.
            </div>
          )}
        </>
      )}
    </div>
  );
};

interface NavCardProps {
  title: string;
  description: string;
  to: string;
}

const NavCard: React.FC<NavCardProps> = ({ title, description, to }) => (
  <Link to={to} style={{ ...cardLinkStyle, ...cardStyle }}>
    <h3 style={{ margin: 0, fontSize: 16, color: "#0078d4" }}>{title}</h3>
    <p style={{ margin: "6px 0 0", fontSize: 13.5, color: "#605e5c", lineHeight: 1.5 }}>
      {description}
    </p>
  </Link>
);

// ── Styles ──────────────────────────────────────────────────────────────────
const pageStyle: React.CSSProperties = {
  padding: 32,
  fontFamily: "Segoe UI, sans-serif",
  maxWidth: 900,
  margin: "0 auto",
};

const sectionHeadingStyle: React.CSSProperties = {
  marginTop: 32,
  marginBottom: 12,
  paddingBottom: 6,
  borderBottom: "1px solid #e1dfdd",
  color: "#323130",
  fontSize: 18,
};

const cardListStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
  gap: 14,
};

const cardStyle: React.CSSProperties = {
  border: "1px solid #e1dfdd",
  borderRadius: 4,
  padding: 16,
  background: "white",
};

const cardLinkStyle: React.CSSProperties = {
  display: "block",
  textDecoration: "none",
  color: "inherit",
};

const errorBoxStyle: React.CSSProperties = {
  background: "#fde7e9",
  border: "1px solid #d13438",
  borderRadius: 4,
  padding: "12px 16px",
  color: "#a4262c",
  fontSize: 13,
  display: "flex",
  alignItems: "center",
  gap: 12,
  marginTop: 16,
};

const retryBtnStyle: React.CSSProperties = {
  background: "#a4262c",
  color: "white",
  border: "none",
  padding: "5px 12px",
  fontSize: 12.5,
  borderRadius: 3,
  cursor: "pointer",
  fontFamily: "inherit",
  marginLeft: "auto",
};

const noRoleNoteStyle: React.CSSProperties = {
  marginTop: 24,
  padding: "14px 16px",
  background: "#f3f3f3",
  borderRadius: 4,
  fontSize: 13.5,
  color: "#605e5c",
  lineHeight: 1.6,
};

export default HomePage;
